import { useContext, useState } from 'react';
import styled, { ThemeContext } from 'styled-components';
import { color } from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import { transparentize } from 'polished';
import { useTranslation } from 'react-i18next';

import useSort from '../../../../../shared/Sort/useSort';
import { TileImage } from '../StyledComponents';
import Text from '../../../../../shared/Text';
import { Button } from '../../../../../shared/FormStyles/Button';
import CardTabs from '../../../../../shared/CardTabs';
import TableHead from './TableHead.jsx';
import { useGetQuestions } from '../../data.jsx';
import StatusSelect from './StatusSelect.jsx';
import Tooltip from '../../../../../shared/Tooltip';
import { QA_TABS } from '../../utils/constants';
import defaultAttrs from '../../../../../lib/utils/defaultAttrs';

const QuestionTable = ({ questionTileId, selectedTab: initialTab, onBack }) => {
  const { t } = useTranslation('reports');
  const [activeTab, setActiveTab] = useState(initialTab);
  const { sortingStatus, changeSort, clearSort } = useSort();
  const { questions, isLoading, tile } = useGetQuestions({
    tileId: questionTileId,
    status: activeTab,
    ...sortingStatus,
  });
  const { colors } = useContext(ThemeContext);

  const handleActiveTab = name => {
    clearSort();
    setActiveTab(name);
  };

  const tabs = [
    {
      name: QA_TABS.allQuestions,
      content: t('questionsTable.tabs.allQuestions', {
        allQuestions: tile.allQuestions,
      }),
    },
    {
      name: QA_TABS.new,
      content: (
        <>
          <StatusIcon bg="notification" />
          {t('questionsTable.tabs.newQuestions', {
            newQuestions: tile.newQuestions,
          })}
        </>
      ),
    },
    {
      name: QA_TABS.contacted,
      content: (
        <>
          <StatusIcon bg="warning.default" />
          {t('questionsTable.tabs.contacted', {
            contactedQuestions: tile.contactedQuestions,
          })}
        </>
      ),
    },
    {
      name: QA_TABS.answered,
      content: (
        <>
          <StatusIcon bg="success.default" />
          {t('questionsTable.tabs.answered', {
            answeredQuestions: tile.answeredQuestions,
          })}
        </>
      ),
    },
  ];

  const contactedInfoLink = question => {
    if (question.contactInfo.includes('@')) {
      return `mailto:${question.contactInfo}?subject=Response to your question on ${tile.demo}&body="${question.question}"`;
    } else if (question.contactInfo.replace(/\D/g, '').length >= 7) {
      return `tel:${question.contactInfo}`;
    }

    return null;
  };

  return (
    <Container>
      <TableHeader>
        <TileImage src={tile.tileImage} />
        <Text m={0} color="black.100" variant="h6" as="h2">
          {tile.tileHeadline}
        </Text>
        <Assignee color="black.80" bg="white" variant="small1">
          {t('questionsTable.header.assignee', {
            assignee: tile.assignee,
          })}
        </Assignee>
        <ReturnButton variant="tertiary" onClick={onBack}>
          <ArrowLeftIcon />
          {t('questionsTable.header.return')}
        </ReturnButton>
      </TableHeader>
      <div>
        <TabsContainer>
          <CardTabs
            tabs={tabs}
            activeTab={activeTab}
            setActiveTab={handleActiveTab}
          />
        </TabsContainer>
        {questions.length > 0 ? (
          <Table>
            <colgroup>
              <col span="1" style={{ width: '10%' }} />
              <col span="1" style={{ width: '10%' }} />
              <col span="1" style={{ width: '10%' }} />
              <col span="1" style={{ width: '50%' }} />
              <col span="1" style={{ width: '10%' }} />
              <col span="1" style={{ width: '10%' }} />
            </colgroup>
            <TableHead sortingStatus={sortingStatus} changeSort={changeSort} />
            <tbody>
              <Tr style={{ visibility: 'collapse' }}>
                <Td>
                  <Tooltip
                    id="tooltip-duration"
                    padding="8px 16px"
                    withBoxShadow={false}
                  />
                </Td>
              </Tr>
              {questions.map(question => (
                <Tr key={question.id}>
                  <Td>
                    <Text color="black.100" variant="small2">
                      {question.date}
                    </Text>
                  </Td>
                  <Td>
                    <Text color="black.100" variant="small2">
                      {question.userName || ''}
                    </Text>
                  </Td>
                  <Td>
                    {contactedInfoLink(question) ? (
                      <ContactInfo
                        as="a"
                        href={contactedInfoLink(question)}
                        color="primary.100"
                        variant="small2"
                      >
                        {question.contactInfo}
                      </ContactInfo>
                    ) : (
                      <Text variant="small2" color="black.100">
                        {question.contactInfo}
                      </Text>
                    )}
                  </Td>
                  <Td>
                    <Text color="black.100" variant="small2">
                      {question.question}
                    </Text>
                  </Td>
                  <Td
                    data-text-color={colors.white}
                    data-background-color={colors.black[100]}
                    data-tip={question.durationInfo}
                    data-for="tooltip-duration"
                  >
                    <DurationWrapper>
                      <ClockIcon />
                      <Text color="black.100" variant="small2">
                        {question.duration}
                      </Text>
                    </DurationWrapper>
                  </Td>
                  <Td>
                    <StatusSelect
                      status={question.status}
                      questionId={question.id}
                      tileId={questionTileId}
                      activeTab={activeTab}
                      sortBy={sortingStatus.sortBy}
                      sortDirection={sortingStatus.sortDirection}
                    />
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <Placeholder>
            {!isLoading && t('questionsTable.placeholder')}
          </Placeholder>
        )}
      </div>
    </Container>
  );
};

const Container = styled.div`
  border-radius: 8px;
  border: 1px solid ${themeGet('colors.black.10')};
`;

const TableHeader = styled.div`
  padding: 16px 24px;
  background: ${themeGet('colors.black.5')};
  display: flex;
  align-items: center;
`;

const ReturnButton = styled(Button)`
  margin: 0 0 0 auto;
  padding: 8px 16px 8px 8px;
`;

const ArrowLeftIcon = styled.i.attrs(
  defaultAttrs({
    className: 'fa-regular fa-arrow-left',
  }),
)`
  margin-right: 4px;
`;

const ClockIcon = styled.i.attrs(
  defaultAttrs({
    className: 'fa-regular fa-clock',
  }),
)`
  margin-right: 4px;
`;

const ContactInfo = styled(Text)`
  cursor: pointer;
`;

const DurationWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const Assignee = styled(Text)`
  border-radius: 100px;
  border: 1px solid ${themeGet('colors.black.60')};
  padding: 5px 8px;
  margin: 0 0 0 8px;
`;

const TabsContainer = styled.div`
  padding: 16px 16px 0 16px;
  border-top: 1px solid ${themeGet('colors.black.10')};
  display: flex;
`;

const Table = styled.table`
  border-top: 1px solid ${themeGet('colors.black.10')};
`;

const StatusIcon = styled.div`
  ${color};
  height: 8px;
  width: 8px;
  border-radius: 50%;
  margin: 0 4px 0 0;
`;

const Tr = styled.tr`
  border-bottom: 1px solid ${themeGet('colors.black.10')};

  &:last-child {
    border: none;
  }

  &:hover {
    background: ${props =>
      transparentize(0.6, themeGet('colors.primary.10')(props))} !important;

    i.fa-clock {
      color ${themeGet('colors.primary.100')};
    }
  }
`;

const Td = styled.td`
  text-align: left;
  padding: 16px 16px 16px 0;
  border: none !important;

  &:first-child {
    padding-left: 40px;
  }
  &:last-child {
    position: relative;
  }
`;

const Placeholder = styled.div`
  padding: 16px;
  border-top: 1px solid ${themeGet('colors.black.10')};
`;

export default QuestionTable;
